import React, { useContext, useMemo } from 'react';
import { Grid, Card, CardHeader, CardContent, Box } from '@mui/material';
import { useInpatientsData } from '../../hooks/useInpatientsData';
import { useInpatientHubData } from '../../hooks/useInpatientHubData';
import { useHospitalStatsData } from '../../hooks/useHospitalStatsData';
import { analyticsCharts } from '../../config/analyticsCharts';
import LoadingState from '../../common/LoadingState';
import ErrorState from '../../common/ErrorState';
import ChartFactory from '../../charts/ChartFactory';
import HospitalStats from '../../HospitalStats';
import InpatientCurrentSituationSection from '../../charts/InpatientCurrentSituationSection';
import InpatientPeriodActivitySection from '../../charts/InpatientPeriodActivitySection';
import DemographicsDashboard from '../../charts/DemographicsCharts';
import { LocalizeContext, LocalizeContextProps } from 'react-localize-redux';
import { IDepartment } from '../../../../../constants/types';

interface ChartConfig {
  id: string;
  source: string;
  order: number;
  title: string;
  type: 'bar' | 'line' | 'pie' | 'doughnut' | 'gauge' | 'demographic' | 'data-only' | 'data-value';
  cols: number;
  visible: boolean;
  debug?: boolean;
}

interface TabProps {
  investigation: {
    permissions: string[];
    [key: string]: any;
  };
  departmentSelected: string;
  startDate: number | null;
  endDate: number | null;
  departments: IDepartment[];
}

const InPatientsTab: React.FC<TabProps> = ({ 
  investigation, 
  departmentSelected, 
  startDate, 
  endDate, 
  departments,
}) => {
  const visibleCharts = useMemo(() => 
    (analyticsCharts.inpatients as ChartConfig[] || [])
      .filter(chart => chart.visible)
      .sort((a, b) => a.order - b.order),
    [analyticsCharts.inpatients]
  );
  
  const debugCharts = useMemo(() => 
    (analyticsCharts.inpatients as ChartConfig[] || [])
      .filter(chart => chart.debug)
      .sort((a, b) => a.order - b.order),
    [analyticsCharts.inpatients]
  );

  const defaultDepartment = useMemo(() =>
    departments && departments.length > 0 ? departments[0] : null,
    [departments]
  );

  // SHER-828: getTranslate is a selector, not a hook; LocalizeContext is missing from the
  // package's typings. DemographicsDashboard takes a resolved string, not a <Translate/>.
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);

  const { stats: hospitalStats, loading: hospitalStatsLoading } = useHospitalStatsData({
    investigation,
    startDate,
    endDate,
  });

  const {
    currentSituation,
    periodActivity,
    demographics,
    demographicsStatus,
    loadingCurrent,
    loadingPeriod,
    error: hubError,
  } = useInpatientHubData({
    investigation,
    departmentSelected,
    startDate,
    endDate,
  });

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {loadingCurrent ? <LoadingState /> : (
            hubError ? <ErrorState error={hubError} /> : (
              <InpatientCurrentSituationSection data={currentSituation} />
            )
          )}
        </Grid>
        <Grid item xs={12}>
          {loadingPeriod ? <LoadingState /> : (
            <InpatientPeriodActivitySection data={periodActivity} />
          )}
        </Grid>
        {/*
          SHER-897. The same component the Outpatients tab renders, fed from the same
          period-activity response the "Patients uniques" tile above reads — so the section and
          the tile describe one population by construction, which is what the ticket asks for.
          It is NOT wired through useInpatientsData: a second request is a second moment, and
          the two figures would be free to disagree again.
        */}
        <Grid item xs={12}>
          {demographicsStatus.status === 'pending' && <LoadingState />}
          {demographicsStatus.status === 'error' && <ErrorState error={demographicsStatus.error} />}
          {demographicsStatus.status === 'ok' && (
            <DemographicsDashboard
              title={translate('hospital.analytics.charts.inpatients.demographics') as string}
              data={demographics}
              // SHER-992. Present only when the decrypt resolved some but not all of the
              // patients the endpoint named, so the card can qualify its figures instead of
              // presenting a shortfall as the population.
              counted={demographicsStatus.counted}
              expected={demographicsStatus.expected}
            />
          )}
        </Grid>
        {visibleCharts.map((chart) => (
          <Grid
            item
            xs={12}
            sm={chart.cols === 12 ? 12 : 6}
            md={chart.cols}
            key={chart.id}
          >
            <ChartWrapper
              chart={chart}
              investigation={investigation}
              departmentSelected={departmentSelected}
              startDate={startDate}
              endDate={endDate}
              departments={departments}
              defaultDepartment={defaultDepartment}
            />
          </Grid>
        ))}
        <Grid item xs={12}>
          <HospitalStats
            loading={hospitalStatsLoading}
            departmentSelected={departmentSelected}
            stats={hospitalStats}
          />
        </Grid>
      </Grid>
      <>
        {debugCharts.map((chart) => (
          <DebugChartWrapper
            key={chart.id}
            chart={chart}
            investigation={investigation}
            departmentSelected={departmentSelected}
            startDate={startDate}
            endDate={endDate}
          />
        ))}
      </>
    </>
  );
};

interface ChartWrapperProps {
  chart: ChartConfig;
  investigation: TabProps['investigation'];
  departmentSelected: string;
  startDate: number | null;
  endDate: number | null;
  departments: IDepartment[];
  defaultDepartment: IDepartment | null;
}

const ChartWrapper: React.FC<ChartWrapperProps> = ({
  chart,
  investigation,
  departmentSelected,
  startDate,
  endDate,
  departments,
  defaultDepartment,
}) => {
  const { data, loading, error } = useInpatientsData({
    investigation,
    departmentSelected,
    startDate: startDate?.toString() || '',
    endDate: endDate?.toString() || '',
    requestedMethod: chart.source as any
  });

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  return (
    <ChartFactory
      id={chart.id}
      type={chart.type}
      title={chart.title}
      data={data}
      departments={departments}
      departmentSelected={defaultDepartment}
    />
  );
};

const DebugChartWrapper: React.FC<Omit<ChartWrapperProps, 'departments' | 'defaultDepartment'>> = ({
  chart,
  investigation,
  departmentSelected,
  startDate,
  endDate,
}) => {
  const { data, loading, error } = useInpatientsData({
    investigation,
    departmentSelected,
    startDate: startDate?.toString() || '',
    endDate: endDate?.toString() || '',
    requestedMethod: chart.source as any
  });

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  return (
    <Card>
      <CardHeader title={chart.id} subheader={chart.source} />
      <CardContent>
        <Box>
          {data && (
            <pre style={{ 
              overflow: 'auto', 
              maxHeight: '100%',
              margin: 0,
              padding: '1rem',
              background: '#f5f5f5',
              borderRadius: '4px'
            }}>
              {JSON.stringify(data, null, 2)}
            </pre>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default InPatientsTab;